import { lazy, Suspense } from 'react';
import { useIsClient } from 'minimal-shared/hooks';
import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { chartClasses } from './classes';
import { ChartLoading } from './components';



// ----------------------------------------------------------------------

const LazyChart = lazy(() =>
  import('react-apexcharts').then((module) => ({ default: module.default }))
);

/**
 * `.jsx` 시절 JSDoc `@typedef ChartProps` 가 소비처에 흘려 주던 계약을 **같은 모양으로**
 * 옮긴 것이다. 선택 prop 은 그대로 선택으로 두고, 나머지는 루트로 통과하므로
 * `Record<string, any>` 로 열어 둔다 — 좁히면 남의 화면이 깨진다.
 */
export type ChartProps = {
  /** apexcharts 차트 종류 (line · bar · pie …) */
  type: string;
  series: any[];
  options: any;
  /** `loading`만 읽는다 */
  slotProps?: any;
  className?: string;
  sx?: any;
} & Record<string, any>;

/**
 * ApexCharts 래퍼.
 *
 * ⚠️ **차트 높이는 `options.chart.height`가 정한다.** 루트는 `styled('div')`라
 * `height` prop을 읽지 않는다 — 넘겨도 div의 속성으로만 남는다.
 */
export function Chart({ type, series, options, slotProps, className, sx, ...other }: ChartProps) {
  const isClient = useIsClient();

  const renderFallback = () => <ChartLoading type={type} sx={slotProps?.loading} />;

  return (
    <ChartRoot
      dir="ltr"
      className={mergeClasses([chartClasses.root, className])}
      sx={sx}
      {...other}
    >
      {isClient ? (
        <Suspense fallback={renderFallback()}>
          <LazyChart type={type} series={series} options={options} width="100%" height={options?.chart?.height || "100%"} />
        </Suspense>
      ) : (
        renderFallback()
      )}
    </ChartRoot>
  );
}

// ----------------------------------------------------------------------

const ChartRoot = styled('div')(({ theme }) => ({
  width: '100%',
  flexShrink: 0,
  position: 'relative',
  borderRadius: (theme.shape.borderRadius as number) * 1.5,
}));
